import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { usePublications, Publication } from '../../contexts/PublicationsContext';
import DashboardLayout from '../../components/includes/DashboardLayout';
import AddPublication from './AddPublication';
import {
  FileText,
  Search,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Calendar,
  User,
  Tag
} from 'lucide-react';

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from 'hooks/use-toast';

const PublicationsManager: React.FC = () => {
  const { publications, updatePublication, deletePublication } = usePublications();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewPublication, setViewPublication] = useState<Publication | null>(null);
  const [editingPublication, setEditingPublication] = useState<Publication | null>(null); // State for editing
  const [publicationToDelete, setPublicationToDelete] = useState<string | null>(null); // State for delete confirmation
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleViewPublication = (pub: Publication) => {
    setViewPublication(pub);
    setShowViewModal(true);
  };

  const handleEditPublication = (pub: Publication) => {
    setEditingPublication(pub);
    setShowAddModal(true);
  };

  const handleTogglePublished = async (id: string, currentStatus: boolean) => {
    try {
      await updatePublication(id, { published: !currentStatus });
      toast({
        title: !currentStatus ? "Publication publiée" : "Publication dépubliée",
        description: !currentStatus ? "La publication est maintenant visible." : "La publication a été retirée du site.",
        className: "bg-blue-600 text-white border-none",
      });
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier le statut.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = (id: string) => {
    setPublicationToDelete(id);
  };

  const confirmDelete = async () => {
    if (!publicationToDelete) return;
    try {
      await deletePublication(publicationToDelete);
      toast({
        title: "Publication supprimée",
        description: "La publication a été supprimée avec succès.",
        variant: "destructive",
      });
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la suppression.",
        variant: "destructive",
      });
    } finally {
      setPublicationToDelete(null);
    }
  };

  const filteredPublications = publications.filter(pub => {
    const matchesCategory = selectedTab === 'all' || pub.category === selectedTab;
    const matchesSearch = pub.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pub.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pub.author.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const totalPages = Math.ceil(filteredPublications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPublications = filteredPublications.slice(startIndex, startIndex + itemsPerPage);



  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'actualite': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/20';
      case 'annonce': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/20';
      case 'evenement': return 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 hover:bg-purple-100 dark:hover:bg-purple-900/20';
      case 'formation': return 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200 hover:bg-orange-100 dark:hover:bg-orange-900/20';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'actualite': return 'Actualité';
      case 'annonce': return 'Annonce';
      case 'evenement': return 'Événement';
      case 'formation': return 'Formation';
      default: return category;
    }
  };

  return (
    <DashboardLayout>
      <div className="flex-1 space-y-4 p-4 pt-6">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Gestion des Publications</h2>
            <p className="text-muted-foreground">
              Créez, modifiez et gérez les contenus du site ISTMECH.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="mr-2 h-4 w-4" /> Nouvelle Publication
            </Button>
          </div>
        </div>

        <Tabs defaultValue="all" value={selectedTab} onValueChange={setSelectedTab} className="space-y-4">
          <Card className="p-4">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="relative w-full sm:w-96">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
              <TabsList>
                <TabsTrigger value="all">Toutes</TabsTrigger>
                <TabsTrigger value="actualite">Actualités</TabsTrigger>
                <TabsTrigger value="annonce">Annonces</TabsTrigger>
                <TabsTrigger value="evenement">Événements</TabsTrigger>
                <TabsTrigger value="formation">Formations</TabsTrigger>
              </TabsList>
            </div>
          </Card>

          <TabsContent value={selectedTab} className="space-y-4">
            <Card>
              <div className="rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Titre</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Auteur</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPublications.length > 0 ? (
                      currentPublications.map((pub) => (
                        <TableRow key={pub.id}>
                          <TableCell className="font-medium">
                            <div className="flex flex-col">
                              <span>{pub.title}</span>
                              <span className="text-xs text-muted-foreground truncate max-w-[200px]">{pub.excerpt}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`border-0 ${getCategoryColor(pub.category)}`}>
                              {getCategoryLabel(pub.category)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={`border-0 ${pub.published
                                ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 hover:bg-green-100'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-100'}`}
                            >
                              {pub.published ? 'Publié' : 'Brouillon'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center text-sm text-muted-foreground">
                              <User className="mr-1 h-3 w-3" /> {pub.author}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center text-sm text-muted-foreground">
                              <Calendar className="mr-1 h-3 w-3" />
                              {new Date(pub.createdAt).toLocaleDateString('fr-FR')}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <span className="sr-only">Open menu</span>
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleViewPublication(pub)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  Voir</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleTogglePublished(pub.id, pub.published)}>
                                  {pub.published ? <EyeOff className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
                                  {pub.published ? 'Dépublier' : 'Publier'}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleEditPublication(pub)}>
                                  <Edit className="mr-2 h-4 w-4" /> Modifier
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => handleDelete(pub.id)}>
                                  <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="h-24 text-center">
                          <div className="flex flex-col items-center justify-center py-6 text-center">
                            <div className="rounded-full bg-muted/30 p-4 mb-4">
                              <FileText className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-semibold">Aucune publication</h3>
                            <p className="text-sm text-muted-foreground max-w-sm mt-1">
                              Aucune publication ne correspond à vos filtres.
                            </p>
                            <Button variant="outline" className="mt-4" onClick={() => { setSearchTerm(''); setSelectedTab('all') }}>
                              Réinitialiser les filtres
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="p-4 border-t text-xs text-muted-foreground">
                Affichage de <strong>{filteredPublications.length}</strong> sur <strong>{publications.length}</strong> publications
              </div>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          isActive={currentPage === page}
                          onClick={() => setCurrentPage(page)}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}

                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Add Publication Modal */}
        {showAddModal && (
          <AddPublication
            onClose={() => setShowAddModal(false)}
            initialData={editingPublication || undefined}
            onSuccess={() => {
              setShowAddModal(false);
              toast({
                title: editingPublication ? "Publication modifiée" : "Publication créée",
                description: editingPublication ? "La publication a été mise à jour avec succès." : "La publication a été ajoutée avec succès.",
                className: "bg-green-600 text-white border-none",
              });
            }}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!publicationToDelete} onOpenChange={(open) => !open && setPublicationToDelete(null)}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Confirmer la suppression</DialogTitle>
              <DialogDescription>
                Êtes-vous sûr de vouloir supprimer cette publication ? Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPublicationToDelete(null)}>Annuler</Button>
              <Button variant="destructive" onClick={confirmDelete}>Supprimer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Publication Modal */}
        {viewPublication && (
          <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
              <DialogHeader>
                <DialogTitle className="text-2xl">{viewPublication.title}</DialogTitle>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className={getCategoryColor(viewPublication.category)}>
                    {getCategoryLabel(viewPublication.category)}
                  </Badge>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-sm text-muted-foreground flex items-center">
                    <Calendar className="mr-1 h-3 w-3" />
                    {new Date(viewPublication.createdAt).toLocaleDateString('fr-FR')}
                  </span>
                  <span className="text-muted-foreground">•</span>
                  <span className="text-sm text-muted-foreground flex items-center">
                    <User className="mr-1 h-3 w-3" />
                    {viewPublication.author}
                  </span>
                </div>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto pr-4">
                <div className="space-y-4 py-4">
                  {viewPublication.imageUrl && (
                    <div className="rounded-lg overflow-hidden border">
                      <img
                        src={viewPublication.imageUrl}
                        alt={viewPublication.title}
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          if (e.currentTarget.parentElement) {
                            e.currentTarget.parentElement.style.display = 'none';
                          }
                        }}
                      />
                    </div>
                  )}

                  {viewPublication.excerpt && (
                    <div className="bg-muted p-4 rounded-lg italic text-muted-foreground border-l-4 border-primary/50">
                      {viewPublication.excerpt}
                    </div>
                  )}

                  <div className="prose dark:prose-invert max-w-none text-sm leading-relaxed">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        table: ({ node, ...props }) => (
                          <div className="my-4 w-full overflow-y-auto rounded-lg border">
                            <table className="w-full text-sm" {...props} />
                          </div>
                        ),
                        thead: ({ node, ...props }) => (
                          <thead className="bg-[#1e293b] text-white [&_tr]:border-b" {...props} />
                        ),
                        tbody: ({ node, ...props }) => (
                          <tbody className="[&_tr:last-child]:border-0" {...props} />
                        ),
                        tr: ({ node, ...props }) => (
                          <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted even:bg-muted/20" {...props} />
                        ),
                        th: ({ node, ...props }) => (
                          <th className="h-12 px-4 text-left align-middle font-medium text-white/90 [&:has([role=checkbox])]:pr-0 border-r last:border-r-0 border-slate-700" {...props} />
                        ),
                        td: ({ node, ...props }) => (
                          <td className="p-4 align-middle [&:has([role=checkbox])]:pr-0 border-r last:border-r-0 border-muted" {...props} />
                        ),
                      }}
                    >
                      {viewPublication.content}
                    </ReactMarkdown>
                  </div>

                  {viewPublication.tags && viewPublication.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-4 border-t mt-4">
                      {viewPublication.tags.map((tag: string, i: number) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          <Tag className="mr-1 h-3 w-3" /> {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center border-t pt-4 mt-2">
                <div className="text-xs text-muted-foreground">
                  Dernière mise à jour: {new Date(viewPublication.updatedAt).toLocaleDateString('fr-FR')}
                </div>
                <Button variant="outline" onClick={() => setShowViewModal(false)}>Fermer</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </DashboardLayout >
  );
};

export default PublicationsManager;
